const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/**
 * The local day a period boundary belongs to, rounded to the nearest midnight: a month that starts
 * at 00:00 SAST starts at 23:00 the day before for a viewer in the UK (and the day's points are
 * grouped by local day), so a plain start-of-day would count from the wrong day.
 */
const nearestDay = (d: Date) => startOfDay(new Date(d.getTime() + 12 * 3_600_000));

/**
 * OE-265 idea 3: a faint line on the 7R graph showing an earlier period (the same weekday last week,
 * or last month), counted exactly like the main line, then moved onto this period's timeline.
 */

export interface ComparisonPoint {
    date: Date;
    valueCompounded: number;
    value?: number;
}

/**
 * The earlier period's points moved forward by `days` (moment-free: calendar days, so a 7-day shift
 * keeps the wall-clock time), led by a zero point so the line rises from R0 like the main one.
 * Copies; the inputs are left alone.
 */
export function shiftOntoPeriod<T extends ComparisonPoint>(points: readonly T[], days: number): T[] {
    if (!points.length) return [];
    const moved = points.map((p) => {
        const date = new Date(p.date);
        date.setDate(date.getDate() + days);
        return { ...p, date };
    });
    return [{ ...moved[0], valueCompounded: 0, value: 0 }, ...moved];
}

/** The switch's words while it is off: what the faint line will show by default. */
export function comparisonSwitchText(view: 'day' | 'month'): string {
    return view === 'day' ? 'Compare with last week' : 'Compare with last month';
}

/**
 * The period the faint line shows, as the date button and the key name it: a day ("Thu 22 Oct"), or a
 * company month by its days ("26 Sep - 25 Oct"), since it seldom matches a calendar month.
 */
export function comparisonPeriodName(periodStart: Date, view: 'day' | 'month', periodEnd?: Date): string {
    // Spelled out: locales disagree on short months (Node's en-GB writes 'Sept').
    const dayMonth = (d: Date) =>
        `${d.getDate()} ${'Jan Feb Mar Apr May Jun Jul Aug Sep Oct Nov Dec'.split(' ')[d.getMonth()]}`;
    if (view === 'day')
        return `${'Sun Mon Tue Wed Thu Fri Sat'.split(' ')[periodStart.getDay()]} ${dayMonth(periodStart)}`;
    return periodEnd
        ? `${dayMonth(nearestDay(periodStart))} - ${dayMonth(periodEnd)}`
        : periodStart.toLocaleDateString('en-GB', { month: 'long' });
}

/** The faint line's key: which period it is, and where it finished. */
export function comparisonCaption(periodStart: Date, view: 'day' | 'month', total: number, periodEnd?: Date): string {
    return `${comparisonPeriodName(periodStart, view, periodEnd)}: R${Math.round(total).toLocaleString('en-US')}`;
}

/**
 * The calendar month a viewer picks stands for the company month that ENDS in it (picking October
 * means 26 Sep - 25 Oct): its 15th lies inside that company month whatever day the months turn on.
 */
export function companyMonthFor(pickedMonth: Date): Date {
    return new Date(pickedMonth.getFullYear(), pickedMonth.getMonth(), 15);
}

/** Whole calendar days from `then` to `now` (a summer-time change makes one day 23 or 25 hours). */
export function daysBetween(then: Date, now: Date): number {
    return Math.round((startOfDay(now).getTime() - startOfDay(then).getTime()) / 86_400_000);
}

/** How many working days come before `day`, counting from `start`'s day. */
function workingDaysBefore(start: Date, day: Date, isWorkingDay: (d: Date) => boolean): number {
    let n = 0;
    for (const d = startOfDay(start); d < startOfDay(day); d.setDate(d.getDate() + 1)) if (isWorkingDay(d)) n++;
    return n;
}

/** The `n`th working day (0 = the first) on or after `start`'s day. */
function nthWorkingDay(start: Date, n: number, isWorkingDay: (d: Date) => boolean): Date {
    const d = startOfDay(start);
    for (let seen = isWorkingDay(d) ? 0 : -1; seen < n || !isWorkingDay(d); ) {
        d.setDate(d.getDate() + 1);
        if (isWorkingDay(d)) seen++;
    }
    return d;
}

/**
 * Last month's points laid over this month: the nth working day then lands on the nth working day
 * now (pass `() => true` when every day counts), keeping each point's time of day. Days past this
 * month's last working day land on it, so the line still ends at last month's total. Led by a zero
 * point, like {@link shiftOntoPeriod}. Copies.
 */
export function alignOntoPeriod<T extends ComparisonPoint>(
    points: readonly T[],
    thenStart: Date,
    nowStart: Date,
    nowEnd: Date,
    isWorkingDay: (d: Date) => boolean
): T[] {
    const thenDay = nearestDay(thenStart);
    const nowDay = nearestDay(nowStart);
    const lastDay = startOfDay(nowEnd);
    while (lastDay > nowDay && !isWorkingDay(lastDay)) lastDay.setDate(lastDay.getDate() - 1);
    const moved: T[] = [];
    for (const p of points) {
        const at = new Date(p.date);
        const day = nthWorkingDay(nowDay, workingDaysBefore(thenDay, at, isWorkingDay), isWorkingDay);
        const date =
            day > lastDay ? new Date(lastDay) : new Date(day.getTime() + (at.getTime() - startOfDay(at).getTime()));
        moved.push({ ...p, date });
    }
    return moved.length ? [{ ...moved[0], valueCompounded: 0, value: 0 }, ...moved] : [];
}
